import { Injectable } from '@angular/core';
import {
    DirectoryOrganization,
    DistrictProfileModel,
    IntegrationProvider,
    IntegrationVendor,
    SchoolFilterType,
    SchoolProfileModel
} from '../model/entity-profile';
import {Store} from '@ngrx/store';
import {BehaviorSubject, forkJoin, Observable, of as observableOf, throwError} from 'rxjs';
import {catchError, concatMap, filter, last, map, mergeMap, takeWhile} from 'rxjs/operators';

import {environment} from '@env/environment';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest} from '@angular/common/http';
import {State} from '@app/app.reducers';
import {DirectoryUser, INVALID_PASSWORD_ERROR_MESSAGE, INVALID_PASSWORD_STATUS_CODE, User} from '../model/user';
import {copyData, DataAccessRequestModel, genId} from '../model/integration-models';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {ApplicationType} from '@app/views/admin/application-settings/models/application-type.model';
import {Integration} from '@app/views/admin/application-settings/models/integration.model';
import {CredentialsModel} from '@app/views/admin/application-settings/models/credentials.model';
import {GeoRegion, GeoCountry, GeoStateList, StatsByStateList} from '../model/geo';
import {InstitutionsVendorListModel} from '@app/core/model/institutional-model';
import {
    DataRolloverSummaryDatasourceModel,
    SchoolYearRolloverModel,
    SchoolYearRolloverSettingsType,
    SchoolYearRolloverTableDatasourceModel
} from '@app/core/model/rollover-model';
import {
    IEtxHost,
    DEFAULT_DISTRICT_PARENT_DOMAIN,
    HOST_COURSERA,
    HOST_QWIKLABS,
    HOST_AMPLIFY
} from '@app/views/administration/model/host.model';
import {CustomerService} from '@app/core/service/customer.service';
import {SharedPool} from '@app/core/model/shared-pool.model';
import {ETXSubOrganization} from '@app/views/sso/sso-portal.model';
import {DictionaryModel} from '@app/views/admin/application-settings/models/dictionary.model';

@Injectable()
export class OrgsService {

    static SYSADMIN_ID = '00000000-0000-0000-0000-000000000000';
    static GG4L_ORG_ID = '00000000-0000-0000-0000-000000000000';

    static getNowLocalStartDate() {
        return new Date(new Date().toLocaleDateString('en-US'));
    }

    static getSettingsSummary(
        daysList: Date[] = [],
        futureDaysOfDataRolloverSettingMap: Map<string, DataRolloverSummaryDatasourceModel>
    ): DataRolloverSummaryDatasourceModel[] {

        daysList.sort();
        const settingsSummary = [];
        daysList.forEach((endDate: Date) => {
            const endDateTimestamp = new Date(endDate).toLocaleDateString('en-US');
            const summaryData = futureDaysOfDataRolloverSettingMap.get(endDateTimestamp);
            if (summaryData) {
                settingsSummary.push(summaryData);
            }
        });

        return settingsSummary;
    }

    static getDaysList(
        firstSettingType: SchoolYearRolloverSettingsType,
        dataRolloverSettings: SchoolYearRolloverModel[] = [],
        secondSettingType?: SchoolYearRolloverSettingsType
    ): Date[] {
        const daysList = [];
        const daysOfDataRolloverSettingMap = new Map<string, boolean>();
        const nowDate = OrgsService.getNowLocalStartDate();

        dataRolloverSettings.forEach((dataRolloverSetting: SchoolYearRolloverModel) => {
            if (firstSettingType
                && (new Date(dataRolloverSetting[firstSettingType]?.endDateTimestamp) > nowDate)) {

                const endDateTimestamp = new Date(dataRolloverSetting[firstSettingType].endDateTimestamp).toLocaleDateString('en-US');
                const daySettingsSummary = daysOfDataRolloverSettingMap.get(endDateTimestamp);
                if (!daySettingsSummary) {
                    daysOfDataRolloverSettingMap.set(endDateTimestamp, true);
                    daysList.push(endDateTimestamp);
                }
            }

            if (secondSettingType
                && (new Date(dataRolloverSetting[secondSettingType]?.endDateTimestamp) > nowDate)) {

                const endDateTimestamp = new Date(dataRolloverSetting[secondSettingType].endDateTimestamp).toLocaleDateString('en-US');
                const daySettingsSummary = daysOfDataRolloverSettingMap.get(endDateTimestamp);
                if (!daySettingsSummary) {
                    daysOfDataRolloverSettingMap.set(endDateTimestamp, true);
                    daysList.push(endDateTimestamp);
                }
            }
        });

        return daysList;
    }

    static getFutureDaysOfDataRolloverSettingMap(
        settingType: SchoolYearRolloverSettingsType,
        dataRolloverSettings: SchoolYearRolloverModel[],
        previewSetting?: SchoolYearRolloverModel
    ): Map<string, DataRolloverSummaryDatasourceModel> {

        const nowDate = OrgsService.getNowLocalStartDate();
        const futureDaysOfDataRolloverSettingMap = new Map<string, DataRolloverSummaryDatasourceModel>();

        const minEndDate = previewSetting
            ? new Date(previewSetting.endDateTimestamp)
            : null;
        const maxEndDate = previewSetting
            ? OrgsService.getDatePlusNDayDate(previewSetting.endDateTimestamp, previewSetting.duration)
            : null;
        dataRolloverSettings.forEach((dataRolloverSetting: SchoolYearRolloverTableDatasourceModel) => {

            if (settingType
                && (dataRolloverSetting[settingType]?.endDateTimestamp)
                && (new Date(dataRolloverSetting[settingType]?.endDateTimestamp) > nowDate)
                && (!minEndDate || (new Date(dataRolloverSetting[settingType]?.endDateTimestamp) > minEndDate))
                && (!maxEndDate || (new Date(dataRolloverSetting[settingType]?.endDateTimestamp) < maxEndDate))) {

                const shortEndDate = new Date(dataRolloverSetting[settingType].endDateTimestamp).toLocaleDateString('en-US');
                let daySettingsSummary = futureDaysOfDataRolloverSettingMap.get(shortEndDate);
                if (daySettingsSummary) {
                    ++daySettingsSummary.countOfDistricts;
                    daySettingsSummary.orgStatTotalCount += dataRolloverSetting.total;
                } else {
                    daySettingsSummary = new DataRolloverSummaryDatasourceModel(
                        dataRolloverSetting[settingType].endDateTimestamp,
                        1,
                        dataRolloverSetting.total
                    );
                    futureDaysOfDataRolloverSettingMap.set(shortEndDate, daySettingsSummary);
                }
            }
        });
        return futureDaysOfDataRolloverSettingMap;
    }

    static getDatePlusNDayDate(currentDay: Date, nDays: number = 1) {
        return new Date(
            (new Date(currentDay))
                .setDate(currentDay.getDate() + nDays)
        );
    }

    constructor(
        private store: Store<State>,
        private http: HttpClient
    ) {
    }

    addOrganizationAdmin(model: any): Observable<DirectoryOrganization> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });
        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/directory/users`, model, {headers: headers});
    }

    editOrganization(model: any, guid): Observable<DirectoryOrganization> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        return this.http
            .put<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/${guid}`, model, {headers: headers});
    }

    editOrganizationAdmins(model: any, guid: string): Observable<DirectoryOrganization> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        return this.http
            .put<any>(`${environment.datahubBaseURI}/api/v2/directory/users/${guid}`, model, {headers: headers})
            .pipe(
                catchError((error): Observable<any> => {
                    if (error.status === INVALID_PASSWORD_STATUS_CODE) {
                        error.message = INVALID_PASSWORD_ERROR_MESSAGE;
                        return throwError(error);
                    }
                    return this.handleError(error);
                })
            );
    }

    saveOrganization(model: any): Observable<DirectoryOrganization> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations`, model, {headers: headers});
    }

    getOrganizationsByParentOrg(orgGuid: string = OrgsService.SYSADMIN_ID): Observable<DirectoryOrganization[]> {
        return this.http
            .get<DirectoryOrganization[]>(`${environment.datahubBaseURI}/api/v2/directory/organizations?parentOrgGuid=${orgGuid}`);
        // .get<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/${orgGuid}`, {headers: headers});
    }

    deleteOrganization(orgGuid): Observable<any> {
        return this.http
            .delete<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/${orgGuid}`);
    }

    deleteOrganizations(orgGuidList: string[]): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        const request = new HttpRequest(
            'DELETE',
            `${environment.datahubBaseURI}/api/v2/directory/organizations`,
            orgGuidList,
            {
                responseType: 'text' as const,
                headers: headers
            });
        return this.http.request(request)
            .pipe(last());
    }

    getDeleteOrganizationProgress(): Observable<{[key: string]: number}> {
        return this.http
            .get<{[key: string]: number}>(`${environment.datahubBaseURI}/api/v2/directory/organizations/delete/progress`);
    }

    deleteRosterData(orgGuid): Observable<any> {
        return this.http
            .delete<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/truncate/${orgGuid}`);
    }

    deleteOrganizationAdmin(guid, orgGuid): Observable<DirectoryOrganization> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        });
        return this.http
            .delete<any>(`${environment.datahubBaseURI}/api/v2/directory/users/${guid}?orgGuid=${orgGuid}`, {headers: headers});
    }

    getOrg(orgGuid): Observable<DirectoryOrganization> {
        return this.http
            .get<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/${orgGuid}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getETXSubOrganizations(orgGuid: string): Observable<ETXSubOrganization[]> {
        return this.http
            .get<ETXSubOrganization[]>(`${environment.etxBaseURI}/manage/organizations/suborganizations/${orgGuid}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getETXOrgProfile(orgGuid: string): Observable<ETXSubOrganization> {
        return this.http
            .get<ETXSubOrganization>(`${environment.etxBaseURI}/manage/organizations/profile/${orgGuid}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getOrgAdmins(orgGuid): Observable<DirectoryUser[]> {
        return this.http
            .get<DirectoryUser[]>(`${environment.datahubBaseURI}/api/v2/directory/users?orgGuid=${orgGuid}`)
            .pipe(
                catchError(this.handleError)
            );
    }

    getAdmins(orgGuid): Observable<DirectoryOrganization> {
        return this.http
            .get<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations?parentOrgGuid=${orgGuid}`);
        // .get<any>(`${environment.datahubBaseURI}/api/v2/directory/organizations/${orgGuid}`, {headers: headers});
        //
    }

    requestLoadOutOrgDataToEtx(orgGuid): Observable<string> {
        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/${orgGuid}/requestDataForEtx`, {});
    }

    requestLoadOutOrgRosterDataForEtx(orgGuid): Observable<string> {
        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/${orgGuid}/requestRosterDataForEtx`, {});
    }

    requestAppAssignmentForEtx(orgGuid): Observable<string> {
        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/${orgGuid}/requestAppAssignmentForEtx`, {});
    }

    requestLoadOutAllOrgsDataToEtx(): Observable<string> {
        return this.http
            .post<any>(`${environment.datahubBaseURI}/api/v2/requestDataForEtx`, {});
    }

    generateHostUniqueValue(): Observable<string> {
        const sendNewRequest = new BehaviorSubject(null);

        return sendNewRequest.asObservable().pipe(
            concatMap(() => {
                const host = 'd' + genId() + DEFAULT_DISTRICT_PARENT_DOMAIN;

                return this.checkHostUniqueInEtx(host).pipe(
                    filter((result: boolean) => {
                        if (!result) {
                            sendNewRequest.next(null);
                        }
                        return result;
                    }),
                    map(() => host)
                );
            }),
            takeWhile(res => !res, true)
        );
    }

    getDisplayedHostValue(host: string): string {
        if (host === HOST_COURSERA || host === HOST_QWIKLABS || host === HOST_AMPLIFY) {
            return host;
        } else if (host && host.endsWith(DEFAULT_DISTRICT_PARENT_DOMAIN)) {
            return host.slice(0, host.length - 12);
        } else {
            return host || '';
        }
    }

    checkHostUniqueInEtx(host: string): Observable<boolean> {
        const url = `${environment.etxBaseURI}/organizations/host/check?hostName=${host}`;

        if (CustomerService.checkIsSupportSsoAppFeature()) {
            return this.http
                .get<IEtxHost>(url)
                .pipe(
                    map((etxHost: IEtxHost) => etxHost && !etxHost.hostName),
                    catchError(this.handleError)
                );
        } else {
            return observableOf(true);
        }
    }

    getRegions(): Observable<GeoRegion[]> {
        return this.http.get<GeoRegion[]>('./assets/geo-data/regions.json');
    }

    getCountries(): Observable<GeoCountry[]> {
        return this.http.get<GeoCountry[]>('./assets/geo-data/countries.json');
    }

    getStates(): Observable<GeoStateList> {
        return this.http.get<GeoStateList>('./assets/geo-data/states.json');
    }

    getStatsByStates(): Observable<StatsByStateList> {
        return this.http.get<StatsByStateList>('./assets/geo-data/stats-by-states.json');
    }

    convertRegionToFriendlyName(currentRegion: string) {
        return this.getRegions()
            .pipe(
                map((regions: GeoRegion[]) => regions.filter((region: GeoRegion) => region.value === currentRegion)[0].message),
                catchError(this.handleError)
            );
    }

    convertCountryToFriendlyName(currentCountry: string) {
        return this.getCountries()
            .pipe(
                map(countries => countries.filter((country: any) => country.iso === currentCountry)[0]),
                catchError(this.handleError)
            );
    }

    convertStateToFriendlyName(currentCountry: string, currentState: string) {
        return this.getStates()
            .pipe(
                map(states => {
                    if (states[currentCountry]) {
                        return states[currentCountry].filter((state: any) => state.stateIso === currentState)[0];
                    } else {
                        return false;
                    }
                }),
                catchError(this.handleError)
            );
    }

    getOrgConnectedToApp(applicationGuid: string): Observable<InstitutionsVendorListModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/vendor/${applicationGuid}/institutions`;
        return this.http
            .get<InstitutionsVendorListModel[]>(url);
    }

    getProfile(organizationGuid: string, applicationGuid: string): Observable<DistrictProfileModel | SchoolProfileModel> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/${organizationGuid}?application=${applicationGuid}`;
        return this.http
            .get<any>(url)
            .pipe(
                map(item => {
                    if (item) {
                        return item;
                    } else {
                        throw new Error('Organization is not exist');
                    }
                }),
                map(item => {
                    if (item.districtGuid === OrgsService.SYSADMIN_ID) {
                        item.districtGuid = null;
                    }
                    if (item.profileType === 'DISTRICT_PROFILE') {
                        return item as DistrictProfileModel;
                    } else if (item.profileType === 'SCHOOL_PROFILE') {
                        return item as SchoolProfileModel;
                    } else if (item.profileType === 'INDEPENDENT_SCHOOL_PROFILE') {
                        item.independed = true;
                        return item as SchoolProfileModel;
                    } else {
                        return item;
                    }
                }),
                map(item => {
                    item.status = item.syncStatus; return item;
                }),
                catchError(this.handleError)
            );
    }

    getDistrictProfile(districtGuid: string, applicationGuid: string): Observable<DistrictProfileModel> {
        let url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${districtGuid}`;
        if (applicationGuid) {
            url += `?application=${applicationGuid}`;
        }
        return this.http
            .get<DistrictProfileModel>(url)
            .pipe(
                map(item => {
                    item.status = item.syncStatus; return item;
                }),
                catchError(this.handleError)
            );
    }

    getSchoolProfile(districtGuid: string, schoolGuid: string, applicationGuid: string): Observable<SchoolProfileModel> {
        let url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${districtGuid}/schools/${schoolGuid}`;
        if (applicationGuid) {
            url += `?application=${applicationGuid}`;
        }
        return this.http
            .get<SchoolProfileModel>(url)
            .pipe(
                map(item => {
                    item.status = item.syncStatus; return item;
                }),
                catchError(this.handleError)
            );
    }

    getSchoolsOfDistrict(districtGuid: string, filteringType: SchoolFilterType, applicationGuid: string, filterName?: string, pageSize?: number, lastSchoolGuid?: string): Observable<SchoolProfileModel[]> {
        const params = this.getQueryParams({
            application: applicationGuid,
            filter: filteringType,
            schoolsQuery: filterName ? encodeURIComponent(filterName) : filterName,
            size: pageSize,
            after: lastSchoolGuid
        });
        const url = `${environment.datahubBaseURI}/api/v2/institutions/districts/${districtGuid}/schools?${params}`;

        return this.http
            .get<SchoolProfileModel[]>(url)
            .pipe(
                map(items => items.map(item => {
                    item.status = item.syncStatus; return item;
                })),
                catchError(this.handleError)
            );
    }

    getInstitutionCredentials(guid: string, appGuid: string): Observable<CredentialsModel> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/${guid}/${appGuid}/credentials`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getOnBoardingApiCredentials(appGuid: string): Observable<CredentialsModel> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/onboarding/credentials/${appGuid}`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    regenerateOnBoardingApiCredentials(appGuid: string): Observable<CredentialsModel> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/onboarding/credentials/${appGuid}/regenerate`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getSharedPoolCredentials(poolId: string, appGuid: string): Observable<CredentialsModel> {
        const url = `${environment.datahubBaseURI}/api/v2/institutions/shared_pools/${poolId}/${appGuid}/credentials`;
        return this.http
            .get<any>(url)
            .pipe(catchError(this.handleError));
    }

    getIntegrationById(integrationId: string): Observable<Integration> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/${integrationId}`;
        return this.http
            .get<Integration>(url)
            .pipe(
                map((integration: Integration) => {
                    if (integration?.dictionaries) {
                        integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) =>
                            !dictionary?.dictionaryName?.startsWith('deleted_')
                        );
                    }
                    return integration;
                }),
                catchError(this.handleError)
            );
    }

    getApplicationProfile(applicationId): Observable<Application> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/${applicationId}`;
        return this.http
            .get<Application>(url)
            .pipe(catchError(this.handleError));
    }

    /**
     * This method is used in case the user is the vendor
     * @param providerGuid
     * @param currentUser
     * @returns
     */
    getCounterpartiesByProviderId(providerGuid: string, currentUser: User): Observable<any> {
        const applicationGuid = currentUser.preferences.getPreferencesItemValue('currentApp');
        const vendor = new IntegrationVendor();
        vendor.name = currentUser.orgName;
        vendor.guid = currentUser.orgGuid;
        vendor.applicationGuid = applicationGuid;

        return forkJoin(
            observableOf(vendor),
            this.getProfile(providerGuid, applicationGuid)
        )
            .pipe(
                mergeMap(([vOrg, pProf]: [IntegrationVendor, any]) => observableOf([vOrg, IntegrationProvider.valueOf(pProf), 'V-P']))
            );
    }

    getCounterpartiesByIndSchoolId(schoolGuid: string, currentUser: User): Observable<any> {
        const applicationGuid = currentUser.preferences.getPreferencesItemValue('currentApp');
        const vendor = new IntegrationVendor();
        vendor.name = currentUser.orgName;
        vendor.guid = currentUser.orgGuid;
        vendor.applicationGuid = applicationGuid;

        return forkJoin(
            observableOf(vendor),
            this.getProfile(schoolGuid, applicationGuid)
        )
            .pipe(
                mergeMap(([vOrg, pProf]: [IntegrationVendor, any]) => observableOf([vOrg, IntegrationProvider.valueOf(pProf), 'V-P']))
            );
    }

    /**
     * This method is used in case the user is the provider
     * @param applicationGuid
     * @param currentUser
     * @returns
     */
    getCounterpartiesByApplicationId(applicationGuid: string, currentUser: User): Observable<any> {
        const vendor = new IntegrationVendor();
        vendor.name = 'Undefined???';
        vendor.applicationGuid = applicationGuid;

        return forkJoin(
            observableOf(vendor),
            this.getProfile(currentUser.orgGuid, applicationGuid),
            this.getApplicationProfile(applicationGuid)
        )
            .pipe(
                mergeMap(([vOrg, pProf, appProf]: [IntegrationVendor, DistrictProfileModel | SchoolProfileModel, Application]) => {
                    vendor.name = appProf.vendorName;
                    vendor.guid = (appProf.type === ApplicationType.PUBLIC ? appProf.ownerId : null);
                    return observableOf([vOrg, IntegrationProvider.valueOf(pProf), 'P-V']);
                })
            );
    }
    getCounterpartiesByRequest(request: DataAccessRequestModel, orgGuid: string, isVendorViewer: boolean): Observable<any> {
        let direction: string;
        let providerGuid: string;
        const vendor = new IntegrationVendor();
        vendor.name = request.vendor;

        if ((orgGuid === request.senderOrgGuid && isVendorViewer)
            || (orgGuid === request.recipientOrgGuid && !isVendorViewer)) {
            vendor.guid = request.senderOrgGuid;
            providerGuid = request.recipientOrgGuid;
            direction = 'V-P';
        } else {
            vendor.guid = request.recipientOrgGuid;
            providerGuid = request.senderOrgGuid;
            direction = 'P-V';
        }

        return this.getIntegrationById(request.integrationId)
            .pipe(
                mergeMap(integration => {
                    vendor.applicationGuid = integration.applicationId;
                    return forkJoin(
                        observableOf(vendor),
                        this.getProfile(providerGuid, integration.applicationId)
                    );
                }),
                mergeMap(([vOrg, pProf]: [IntegrationVendor, any]) => observableOf([vOrg, IntegrationProvider.valueOf(pProf), direction]))
            );
    }

    getInstitutionAccessToken(guid: string, appGuid: string, isSharedPool = false): Observable<string> {
        return (isSharedPool
                ? this.getSharedPoolCredentials(guid, appGuid)
                : this.getInstitutionCredentials(guid, appGuid)
            ).pipe(
                mergeMap((credentials: CredentialsModel) => this.getAccessToken(credentials)
                    .pipe(
                        map((data: any) => data['access_token']),
                        catchError(this.handleError)
                    )),
                catchError(this.handleError)
            );
    }

    getAccessToken(credentials: CredentialsModel): Observable<any> {
        const url = `${environment.datahubBaseURI}/oauth/token`;

        const authBase64 = btoa(`${credentials.clientId}:${credentials.clientSecret}`);
        const headers = new HttpHeaders({
            'Authorization': `Basic ${authBase64}`,
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        const params = new HttpParams()
            .set('grant_type', 'client_credentials');

        return this.http.post(url, params, {headers: headers})
            .pipe(catchError(this.handleError));
    }

    getAPIDocsConfig(accessToken: string, isGradebookSyncEnabled = false): Observable<any> {
        const headers = new HttpHeaders({
            'Authorization': `Bearer ${accessToken}`
        });
        return this.http
            // .get<any>(`${environment.dataApiBaseURI}/datahub/services/v2/api-docs`, {headers: headers})
            .get<any>(`${environment.dataApiBaseURI}/datahub/services/v3/api-docs`, {headers: headers})
            .pipe(
                map((jsonConfig: any) => this.hideUnusedEndPoints(jsonConfig, isGradebookSyncEnabled)),
                catchError(this.handleError)
            );
    }

    public hideUnusedEndPoints(jsonConfig: any, isGradebookSyncEnabled = false): any {
        if (jsonConfig.paths) {
            const unusedEndPoints = [
                '/ims/oneroster/v1p1/schools/{id}/academicSessions',
                '/ims/oneroster/v1p1/schools/{id}/terms',
                '/ims/oneroster/v1p1/teacherEnrollments',
                '/ims/oneroster/v1p1/schools/{id}/teacherEnrollments'
            ];
            let hiddenEntities = [];
            if (!isGradebookSyncEnabled) {
                hiddenEntities = [
                    '/ims/oneroster/v1p1/categories',
                    '/ims/oneroster/v1p1/gradingPeriods',
                    '/ims/oneroster/v1p1/lineItems',
                    '/{id}/lineItems',
                    '/ims/oneroster/v1p1/results',
                    '/{id}/results'
                ];

                const hiddenTags = ['Category', 'GradingPeriod', 'LineItem', 'Result'];
                jsonConfig.tags.forEach((tag: {name: string}, key: number) => {
                    if (hiddenTags.indexOf(tag.name) > -1) {
                        delete jsonConfig.tags[key];
                    }
                });
            }
            Object.keys(jsonConfig.paths).forEach((path: string) => {
                if ((unusedEndPoints.indexOf(path) > -1)
                    || hiddenEntities.some((entityBasePath: string) => path.indexOf(entityBasePath) > -1)
                ) {
                    delete jsonConfig.paths[path];
                }
            });
        }

        jsonConfig.host = environment.dataApiBaseURI.split('://').pop();
        if (jsonConfig['servers'] && (jsonConfig['servers'].length > 0)) {
            const defaultServer = jsonConfig['servers'].find((server: {description: string, url: string}) =>
                server['description'] === 'Default Server URL'
            );
            if (defaultServer) {
                defaultServer['url'] = `${environment.dataApiBaseURI}/datahub/services/`;
            }
        }

        if (jsonConfig['tags']) {
            // jsonConfig['tags'].forEach((tag: {name: string, description: string}) =>
            //     tag['description'] = null
            // );
            jsonConfig['tags'].sort((a: {name: string, description: string}, b: {name: string, description: string}) =>
                (a['name'] || '').localeCompare(b['name'] || '')
            );
        }

        return jsonConfig;
    }

    public getAPIDocsConfigByVersion(jsonConfig: any, version: string): any {
        const result = copyData(jsonConfig);
        if (result.paths) {
            Object.keys(result.paths).forEach((key: string) => {
                if (key.indexOf(`/${version}/`) === -1) {
                    delete result.paths[key];
                }
            });
        }
        return result;
    }

    getOrgDataRolloverSettings(orgGuid: string): Observable<SchoolYearRolloverModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/school-year-rollover/settings/organizations/${orgGuid}`;
        return this.http
            .get<SchoolYearRolloverModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    saveOrgDataRolloverSettings(dataRolloverSettings: SchoolYearRolloverModel[], orgGuid: string): Observable<SchoolYearRolloverModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/school-year-rollover/settings/organizations/${orgGuid}`;
        return this.http
            .post<SchoolYearRolloverModel[]>(url, dataRolloverSettings)
            .pipe(catchError(this.handleError));
    }

    getBulkDataRolloverSettings(): Observable<SchoolYearRolloverModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/school-year-rollover/settings/organizations`;
        return this.http
            .get<SchoolYearRolloverModel[]>(url)
            .pipe(catchError(this.handleError));
    }

    saveBulkDataRolloverSettings(dataRolloverSettings: SchoolYearRolloverModel[]): Observable<SchoolYearRolloverModel[]> {
        const url = `${environment.datahubBaseURI}/api/v2/school-year-rollover/settings/organizations`;
        return this.http
            .post<SchoolYearRolloverModel[]>(url, dataRolloverSettings)
            .pipe(catchError(this.handleError));
    }

    getVendorsSharedPools(): Observable<SharedPool[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/shared_pools/vendors`;
        return this.http.get<SharedPool[]>(url)
            .pipe(catchError(this.handleError));
    }

    private getQueryParams(params: Object): string {
        return Object.keys(params)
            .filter((key: string) => params[key])
            .map((key: string) => `${key}=${params[key]}`)
            .join('&');
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${error.status}, ` +
                `body was: ${error.message}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}
